import type {MouseEvent, ReactElement} from 'react';
import {useState} from 'react';
import {createPortal} from 'react-dom';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {openPremiumPricingModal} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {GETTY_STOCK_CONTENT_UPSELL} from '@Panels/premium-pricing-panel/premium-pricing-panel.constants';
import {closeStockMediaPopup} from '@Components/poster-editor/components/poster-add-item-bar/components/stock-media-dropdown/stock-media-popup-controller';
import {OUTSIDE_CLICK_IGNORE_ATTRIBUTE} from '@Hooks/useOutsideClick';
import type {SourceTagProps, SourceTagTooltipCSSProperties, SourceTagTooltipPosition} from './source-tag.types';
import {GETTY_SOURCE_TAG_TOOLTIP_ID, SOURCE_TAG_TOOLTIP_VERTICAL_OFFSET, SOURCE_TAG_TOOLTIP_VIEWPORT_MARGIN, SOURCE_TAG_TOOLTIP_WIDTH} from './source-tag.types';
import styles from './source-tag.module.scss';

export function SourceTag({isPremium = false}: SourceTagProps): ReactElement {
  const [tooltipPosition, setTooltipPosition] = useState<SourceTagTooltipPosition | null>(null);

  const getTooltipText = (): string => {
    if (isPremium) {
      return window.i18next.t('pmwjs_getty_premium_included_tooltip_2');
    }
    return window.i18next.t('pmwjs_getty_watermark_upgrade_tooltip');
  };

  const onTooltipTextClick = (event: MouseEvent): void => {
    event.stopPropagation();
    if (isPremium) {
      return;
    }
    if (event.target instanceof HTMLElement && event.target.closest('u') !== null) {
      setTooltipPosition(null);
      closeStockMediaPopup();
      openPremiumPricingModal(false, GETTY_STOCK_CONTENT_UPSELL);
    }
  };

  const getTooltipPositionForTag = (wrapperRect: DOMRect): SourceTagTooltipPosition => {
    const tagCenterLeft = wrapperRect.left + wrapperRect.width / 2;
    const clampedTooltipCenterLeft = Math.max(SOURCE_TAG_TOOLTIP_WIDTH / 2 + SOURCE_TAG_TOOLTIP_VIEWPORT_MARGIN, tagCenterLeft);
    return {
      left: clampedTooltipCenterLeft,
      top: wrapperRect.top - SOURCE_TAG_TOOLTIP_VERTICAL_OFFSET,
      arrowShift: tagCenterLeft - clampedTooltipCenterLeft,
    };
  };

  const getTooltip = (): ReactElement | null => {
    if (!tooltipPosition) {
      return null;
    }
    const tooltipStyle: SourceTagTooltipCSSProperties = {
      left: tooltipPosition.left,
      position: 'fixed',
      top: tooltipPosition.top,
      '--source-tag-tooltip-arrow-shift': `${tooltipPosition.arrowShift}px`,
    };
    return createPortal(
      <div {...{[OUTSIDE_CLICK_IGNORE_ATTRIBUTE]: ''}}>
        <Tooltip
          id={GETTY_SOURCE_TAG_TOOLTIP_ID}
          position={TOOLTIP_POSITION.BOTTOM_MIDDLE}
          className={styles.tooltip}
          style={tooltipStyle}
          text={
            <Text
              className={`${styles.tooltipText} ${isPremium ? styles.tooltipTextPremium : styles.tooltipTextUpgrade}`}
              val={getTooltipText()}
              size={TextSize.MD}
              onClick={onTooltipTextClick}
              dangerouslySetInnerHTML
            />
          }
        />
      </div>,
      document.body
    );
  };

  const getTagContent = (): ReactElement => {
    if (isPremium) {
      return <Icon icon="icon-crown" size={IconSize.SIZE_ICON_12} />;
    }
    return <div className={styles.content} />;
  };

  return (
    <div
      className={`${styles.tagWrapper} ${tooltipPosition ? styles.tagWrapperWithTooltipShown : ''}`}
      onMouseEnter={(event: MouseEvent<HTMLDivElement>): void => {
        setTooltipPosition(getTooltipPositionForTag(event.currentTarget.getBoundingClientRect()));
      }}
      onMouseLeave={(): void => {
        setTooltipPosition(null);
      }}
      onClick={(): void => {
        setTooltipPosition(null);
      }}
    >
      <div className={styles.container}>{getTagContent()}</div>
      {getTooltip()}
    </div>
  );
}
